import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import { collection, query, onSnapshot, orderBy, where, addDoc, serverTimestamp, doc, updateDoc, getDoc, deleteDoc } from "firebase/firestore";
import {
    X,
    Send,
    User,
    Clock,
    Building2,
    Tag,
    AlertCircle,
    CheckCircle2,
    Lock,
    Users,
    Activity,
    Trash2
} from "lucide-react";

export default function TicketDetail({ ticketId, onClose }) {
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState("");
    const [loading, setLoading] = useState(true);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!ticketId) return;

        // Fetch Ticket Header
        const unsubTicket = onSnapshot(doc(db, "tickets", ticketId), (docSnap) => {
            if (docSnap.exists()) {
                setTicket({ id: docSnap.id, ...docSnap.data() });
            }
        });

        // Fetch Messages
        const q = query(
            collection(db, "ticketMessages"),
            where("ticketId", "==", ticketId)
        );

        const unsubMessages = onSnapshot(q, (snapshot) => {
            const msgData = [];
            snapshot.forEach(doc => msgData.push({ id: doc.id, ...doc.data() }));

            // Sort client-side to avoid Firebase composite index errors
            msgData.sort((a, b) => {
                const timeA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0;
                const timeB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0;
                return timeA - timeB; // ascending
            });

            setMessages(msgData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching messages:", error);
            setLoading(false); // Make sure to disable loading even on error
        });

        return () => {
            unsubTicket();
            unsubMessages();
        };
    }, [ticketId]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!reply.trim()) return;

        try {
            await addDoc(collection(db, "ticketMessages"), {
                ticketId,
                senderId: "super_admin",
                message: reply,
                timestamp: serverTimestamp()
            });

            // Update ticket status to waiting_client if it was open
            const updates = {
                status: "waiting_client",
                lastUpdated: serverTimestamp()
            };

            if (!ticket?.firstResponseAt) {
                updates.firstResponseAt = serverTimestamp();
            }

            await updateDoc(doc(db, "tickets", ticketId), updates);

            // Notify Client
            if (ticket?.companyId) {
                await addDoc(collection(db, "Clients", ticket.companyId, "notifications"), {
                    title: "Ticket Update",
                    message: `Admin replied to ticket ${ticket.ticketNumber}`,
                    type: "message",
                    read: false,
                    link: "/client/support",
                    timestamp: serverTimestamp()
                });
            }

            setReply("");
        } catch (error) {
            console.error("Error sending reply:", error);
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        try {
            const updates = {
                status: newStatus,
                lastUpdated: serverTimestamp()
            };

            if (newStatus === "resolved" && !ticket?.resolvedAt) {
                updates.resolvedAt = serverTimestamp();
            }

            await updateDoc(doc(db, "tickets", ticketId), updates);

            // Notify Client
            if (ticket?.companyId) {
                await addDoc(collection(db, "Clients", ticket.companyId, "notifications"), {
                    title: "Ticket Status Changed",
                    message: `Ticket ${ticket.ticketNumber} is now ${newStatus.replace('_', ' ')}`,
                    type: "alert",
                    read: false,
                    link: "/client/support",
                    timestamp: serverTimestamp()
                });
            }
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const handleUpdatePriority = async (newPriority) => {
        try {
            await updateDoc(doc(db, "tickets", ticketId), {
                priority: newPriority,
                lastUpdated: serverTimestamp()
            });
        } catch (error) {
            console.error("Error updating priority:", error);
        }
    };

    const handleUpdateField = async (field, value) => {
        try {
            await updateDoc(doc(db, "tickets", ticketId), {
                [field]: value,
                lastUpdated: serverTimestamp()
            });
        } catch (error) {
            console.error(`Error updating ${field}:`, error);
        }
    };

    const handleDeleteTicket = async () => {
        if (!window.confirm("Are you sure you want to permanently delete this ticket? This action cannot be undone.")) return;
        try {
            await deleteDoc(doc(db, "tickets", ticketId));
            onClose();
        } catch (error) {
            console.error("Error deleting ticket:", error);
            alert("Failed to delete ticket.");
        }
    };

    if (loading) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy-900/40 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right-10 duration-500">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
                    <div className="flex items-center gap-4">
                        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-all text-slate-400">
                            <X size={20} />
                        </button>
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-bold text-navy-900 font-serif">{ticket?.ticketNumber}</h2>
                                <select
                                    value={ticket?.status || "open"}
                                    onChange={(e) => handleUpdateStatus(e.target.value)}
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200 outline-none cursor-pointer hover:bg-blue-100 transition-colors appearance-none pr-6 custom-arrow`}
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%231d4ed8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right 0.2rem center`, backgroundRepeat: `no-repeat`, backgroundSize: `1.2em 1.2em` }}
                                >
                                    <option value="open">Open</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="waiting_client">Waiting Client</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>
                            <p className="text-sm text-slate-500 font-medium">{ticket?.subject}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleDeleteTicket}
                        className="p-2 flex items-center justify-center text-red-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete Ticket"
                    >
                        <Trash2 size={20} />
                    </button>
                </div>

                {/* Sub-Header / Metadata */}
                <div className="p-4 bg-slate-50 border-b border-slate-100 grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-slate-400" />
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Tenant</p>
                            <p className="text-xs font-bold text-navy-900">{ticket?.companyId}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">User</p>
                            <p className="text-xs font-bold text-navy-900">{ticket?.userId || "System Auth"}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-right justify-end">
                        <Tag size={16} className="text-slate-400" />
                        <div className="flex flex-col items-end">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Priority</p>
                            <select
                                value={ticket?.priority || "normal"}
                                onChange={(e) => handleUpdateField("priority", e.target.value)}
                                className={`text-xs font-bold uppercase ${ticket?.priority === 'urgent' ? 'text-rose-600' : 'text-blue-600'} bg-transparent border-none outline-none cursor-pointer hover:underline text-right appearance-none pr-4`}
                                style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right top`, backgroundRepeat: `no-repeat`, backgroundSize: `1.2em 1.2em` }}
                            >
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Extended Metadata (SLA / Agent / Notes) */}
                <div className="p-4 bg-slate-100/50 border-b border-slate-100 flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                            <Users size={16} className="text-slate-400" />
                            <div className="flex-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Assigned To</p>
                                <select
                                    value={ticket?.assignedTo || ""}
                                    onChange={(e) => handleUpdateField("assignedTo", e.target.value)}
                                    className="text-xs font-bold text-navy-900 bg-transparent border-none outline-none cursor-pointer hover:underline w-full appearance-none p-0 custom-arrow pr-4"
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%230f172a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right center`, backgroundRepeat: `no-repeat`, backgroundSize: `1em 1em` }}
                                >
                                    <option value="" disabled>Select Agent</option>
                                    <option value="unassigned">Unassigned</option>
                                    <option value="admin_1">Admin 1</option>
                                    <option value="admin_2">Admin 2</option>
                                    <option value="broker_team">Broker Team</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 justify-end text-right">
                            <Activity size={16} className="text-slate-400" />
                            <div className="flex flex-col items-end">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">SLA Status</p>
                                <p className={`text-xs font-bold ${ticket?.firstResponseAt ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    {ticket?.firstResponseAt ? 'Responded' : 'Awaiting Reply'}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-1">Internal Notes (Hidden from Client)</p>
                        <textarea
                            className="w-full text-xs p-2 rounded-lg bg-white border border-slate-200 outline-none focus:border-navy-900 focus:ring-1 focus:ring-navy-900 resize-none"
                            placeholder="Add private notes here..."
                            rows={2}
                            defaultValue={ticket?.internalNotes || ""}
                            onBlur={(e) => handleUpdateField("internalNotes", e.target.value)}
                        />
                    </div>
                </div>

                {/* Chat Area */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 bg-white custom-scrollbar">
                    {messages.map((msg, i) => {
                        const isSystem = msg.senderId === "system";
                        const isAdmin = msg.senderId === "super_admin" || msg.senderId === "admin";

                        return (
                            <div key={msg.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"} items-start gap-3`}>
                                {!isAdmin && (
                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                        {isSystem ? <AlertCircle size={14} className="text-slate-400" /> : <User size={14} className="text-slate-400" />}
                                    </div>
                                )}
                                <div className={`max-w-[80%] p-4 rounded-2xl shadow-sm text-sm leading-relaxed ${isAdmin ? "bg-navy-900 text-white rounded-tr-none" : "bg-slate-50 text-slate-700 border border-slate-100 rounded-tl-none"
                                    }`}>
                                    <p>{msg.message}</p>
                                    <div className={`text-[10px] mt-2 font-bold uppercase tracking-widest ${isAdmin ? "text-navy-300 text-right" : "text-slate-400"}`}>
                                        {msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '---'}
                                    </div>
                                </div>
                                {isAdmin && (
                                    <div className="w-8 h-8 rounded-full bg-navy-900 flex items-center justify-center flex-shrink-0 border-2 border-white shadow-md">
                                        <Lock size={14} className="text-white" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Status Actions */}
                <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex gap-2">
                    <button
                        onClick={() => handleUpdateStatus("in_progress")}
                        className="flex-1 py-2 text-[10px] font-bold uppercase tracking-widest border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 transition-all bg-white"
                    >
                        Mark In Progress
                    </button>
                    <button
                        onClick={() => handleUpdateStatus("resolved")}
                        className="flex-1 py-2 text-[10px] font-bold uppercase tracking-widest border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-all bg-white"
                    >
                        Mark Resolved
                    </button>
                    <button
                        onClick={() => handleUpdateStatus("closed")}
                        className="flex-1 py-2 text-[10px] font-bold uppercase tracking-widest border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 transition-all bg-white"
                    >
                        Close Ticket
                    </button>
                </div>

                {/* Reply Box */}
                <div className="p-6 pb-12 md:pb-6 border-t border-slate-100 bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
                    <form onSubmit={handleSendReply} className="relative flex items-center">
                        <textarea
                            rows={1}
                            placeholder="Type your official response..."
                            className="w-full pl-4 pr-16 py-4 bg-slate-100 border-none rounded-2xl focus:ring-2 focus:ring-navy-900/10 outline-none resize-none text-sm font-medium"
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply(e)}
                        />
                        <button
                            type="submit"
                            disabled={!reply.trim()}
                            className="absolute right-2 p-3 bg-navy-900 text-white rounded-xl hover:bg-navy-800 disabled:opacity-50 transition-all shadow-lg active:scale-95"
                        >
                            <Send size={18} />
                        </button>
                    </form>
                    <div className="mt-4 flex items-center justify-center gap-4">
                        <span className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-1.5 ring-1 ring-slate-100 px-2 py-0.5 rounded-full">
                            <Clock size={10} /> Avg Response: 12m
                        </span>
                        <span className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-1.5 ring-1 ring-slate-100 px-2 py-0.5 rounded-full">
                            <CheckCircle2 size={10} strokeWidth={3} /> Certified Support
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
